import { ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaTimes } from 'react-icons/fa'
import { IconButton } from '../Button'
import { cx } from '../cx'
import { useDraggableFloatingPanel } from '../../hooks/useDraggableFloatingPanel/useDraggableFloatingPanel'
import s from './styles.module.scss'

export type FloatingPanelProps = {
    /** Icon rendered inside the trigger button. */
    triggerIcon: ReactNode
    /** Optional label next to the trigger icon. */
    triggerLabel?: ReactNode
    /** Accessible name for the trigger and the panel dialog. */
    ariaLabel: string
    /** Panel header title, shown next to the close button. */
    title: ReactNode
    /** Extra controls rendered in the header, before the close button. */
    headerActions?: ReactNode
    /** Panel body content. */
    children: ReactNode
    /** Panel width in px. Defaults to 280. */
    width?: number
    className?: string
}

/**
 * Trigger button + fixed, dockable floating panel. Stays open on outside
 * clicks and doesn't reposition on scroll, closing only via its own trigger,
 * a close button, or Escape. Drag by the header (see useDraggableFloatingPanel).
 *
 * Use-case content lives alongside the caller — e.g. a `*PanelContent.tsx`
 * next to the page that mounts this shell.
 */
export const FloatingPanel = ({
    triggerIcon,
    triggerLabel,
    ariaLabel,
    title,
    headerActions,
    children,
    width = 280,
    className,
}: FloatingPanelProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const { panelRef, handleRef, isDragging } = useDraggableFloatingPanel<HTMLDivElement, HTMLDivElement>(isOpen)

    useEffect(() => {
        if (!isOpen) return
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }
        document.addEventListener('keydown', closeOnEscape)
        return () => document.removeEventListener('keydown', closeOnEscape)
    }, [isOpen])

    return (
        <>
            <button
                type="button"
                className={cx(s.trigger, triggerLabel && s.triggerWithLabel, className)}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                onClick={() => setIsOpen((open) => !open)}
            >
                {triggerIcon}
                {triggerLabel}
            </button>

            {isOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        ref={panelRef}
                        className={s.panel}
                        style={{ width }}
                        role="dialog"
                        aria-label={ariaLabel}
                    >
                        <span className={s.grabber} aria-hidden="true" />
                        <div
                            ref={handleRef}
                            className={cx(s.dragRegion, isDragging ? s.grabbing : s.grab)}
                            aria-label="Drag to move panel"
                        >
                            <div className={s.header}>
                                <h3 className={s.title}>{title}</h3>
                                <div className={s.headerActions}>
                                    {headerActions}
                                    <IconButton
                                        label="Close panel"
                                        icon={<FaTimes size={12} />}
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setIsOpen(false)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={s.body}>{children}</div>
                    </div>,
                    document.body,
                )}
        </>
    )
}
